import { useSelector } from "react-redux";
import {
  useCreateCompanyMutation,
  useDeleteCompanyImageMutation,
  useEditCompanyMutation,
} from "../../../api/companyApi";
import { useUpdateCompanyProfileImageMutation } from "../../../../../_features/company/api/customerApiSlice";
import {
  useCreateSelfserveUserMutation,
  useEditSelfserveUserMutation,
} from "../../../../../_features/selfServeCheckout/api/selfServeCheckoutApiSlice";

/**
 * Data/API Layer - the only place CompanyFormModal's three separate
 * mutation sources (this feature's own company/api/companyApi.js, plus two
 * still-old-tree slices for logo upload and the self-serve edit path) are
 * reached from. Everything else in the modal talks to `submitCompany`, not
 * to RTK Query hooks directly.
 */
export function useCompanyFormMutations() {
  // createSelfserveUser's route has no auth/orgId middleware (it doubles as
  // the public checkout signup endpoint) and 400s without an explicit
  // `orgId` in the body - the admin session's own org, same field
  // `jwt.decode(token).selectedOrg` resolves to server-side.
  const orgId = useSelector((state) => state.auth.user?.selectedOrg);
  const [createCompany, { isLoading: isCreating }] = useCreateCompanyMutation();
  const [createSelfserveUser, { isLoading: isCreatingSelfserveUser }] =
    useCreateSelfserveUserMutation();
  const [updateCompany, { isLoading: isUpdating }] = useEditCompanyMutation();
  const [editSelfserveUser, { isLoading: isEditingSelfserveUser }] =
    useEditSelfserveUserMutation();
  const [uploadLogo] = useUpdateCompanyProfileImageMutation();
  const [deleteLogo] = useDeleteCompanyImageMutation();

  /**
   * Creates or updates a company, then syncs its logo if one was picked or
   * marked for deletion. Same `.unwrap()` sequencing as the original inline
   * handlers - throws on any step's failure, so the caller's try/catch
   * drives the user-facing error toast (this layer only talks to the API,
   * it doesn't decide how failures are presented).
   */
  async function submitCompany({ mode, isSelfServeUser, validData, rowData, imageFile, shouldDeleteImage }) {
    if (mode === "create") {
      // Was always createCompany regardless of isSelfServeUser - every
      // "+ Invite self-serve user" submission silently created a plain
      // Company document instead of a SelfserveUsers one. The admin list
      // (getSelfServeUsers) only ever reads the SelfserveUsers collection,
      // so the new row never appeared back in it even though the modal
      // reported success. Mirrors the isSelfServeUser branch the edit path
      // below already has.
      // createSelfserveUser destructures a plain `email` field (it doubles
      // as the public checkout signup endpoint, which sends one) - but this
      // modal's schema is the Company one, which splits email across
      // companyEmail/individualEmail (see createCompany's own isIndividual
      // ? individualEmail : companyEmail mapping just below). Without this,
      // every self-serve create 400s with "Invalid email format" since
      // `email` is never present in validData.
      const response = isSelfServeUser
        ? await createSelfserveUser({
            ...validData,
            email: validData?.isIndividual ? validData?.individualEmail : validData?.companyEmail,
            industry: validData?.industry?.name,
            orgId,
          }).unwrap()
        : await createCompany(validData).unwrap();
      const companyId = response?.data?._id;

      if (imageFile) {
        if (!companyId) {
          const missingIdError = new Error("Company was created but its id is missing - logo upload skipped.");
          missingIdError.data = { message: missingIdError.message };
          throw missingIdError;
        }
        await uploadLogo({ companyId, imageFile }).unwrap();
      }

      return response;
    }

    const response = isSelfServeUser
      ? await editSelfserveUser({
          ...validData,
          industry: validData?.industry?.name,
          orgId: rowData?.orgId,
          _id: rowData?._id,
        }).unwrap()
      : await updateCompany({
          ...validData,
          orgId: rowData?.orgId,
          _id: rowData?._id,
        }).unwrap();

    if (imageFile && !shouldDeleteImage) {
      await uploadLogo({ companyId: rowData?._id, imageFile }).unwrap();
    } else if (imageFile || shouldDeleteImage) {
      await deleteLogo({ companyId: rowData?._id }).unwrap();
    }

    return response;
  }

  return {
    submitCompany,
    isSubmitting: isCreating || isCreatingSelfserveUser || isUpdating || isEditingSelfserveUser,
  };
}
